import { STARTUP_BURST_MS } from './webrtcLatencyContracts';

/**
 * Host stream warmth.
 *
 * Beacon starts capture before anyone connects, so the startup burst has
 * already settled when a guest joins. Pavilion must use the same predicates:
 * arm capture while the host is joinable, keep it across player churn, and
 * skip a second burst once that window has elapsed.
 */

export type HostJoinAvailability = 'idle' | 'joinable';

export type CaptureStopDecision = 'retain' | 'tear-down';

const HARD_CAPTURE_STOP_REASONS = new Set([
  'host:game_closed',
  'host:operator_stop',
  'host:leave_availability',
  'admin_exit_restart',
]);

/** Capture and display-wake should be up while a player may join at any time. */
export function shouldArmHostStreamWarmth(availability: HostJoinAvailability): boolean {
  return availability === 'joinable';
}

/** Operator, game-exit, and admin-restart stops always drop capture. */
export function isHardCaptureStopReason(reason?: string): boolean {
  if (!reason) return false;
  return HARD_CAPTURE_STOP_REASONS.has(reason);
}

/**
 * Retain capture while joinable unless the reason is a hard stop. Missing and
 * unrecognized reasons also retain capture while joinable; an idle host tears it down.
 */
export function captureStopDecision(input: {
  hostJoinable: boolean;
  reason?: string;
}): CaptureStopDecision {
  if (isHardCaptureStopReason(input.reason)) return 'tear-down';
  if (input.hostJoinable) return 'retain';
  return 'tear-down';
}

/** Return the arm timestamp plus the burst duration, both in milliseconds; does not inspect capture readiness. */
export function pipelineSettledAtMs(warmArmedAtMs: number, burstMs: number = STARTUP_BURST_MS): number {
  return warmArmedAtMs + burstMs;
}

/** Skip the burst at or after the settle instant; missing or nonfinite timestamps return false. */
export function shouldSkipStartupBurst(input: {
  pipelineSettledAtMs: number | null;
  nowMs: number;
}): boolean {
  if (input.pipelineSettledAtMs == null) return false;
  if (!Number.isFinite(input.pipelineSettledAtMs) || !Number.isFinite(input.nowMs)) return false;
  return input.nowMs >= input.pipelineSettledAtMs;
}

/**
 * Pavilion login shell stays asleep while idle so nonsole entry still works.
 * It wakes for an active broadcast and for the joinable wait before streaming.
 * Prime, beacon, and guest already wake at launch.
 */
export function shouldWakeDisplayForJoinableWait(input: {
  launchPreventsSleep: boolean;
  isPavilionLoginShell: boolean;
  broadcastActive: boolean;
  hostJoinable: boolean;
}): boolean {
  if (input.launchPreventsSleep) return true;
  if (!input.isPavilionLoginShell) return false;
  if (input.broadcastActive) return true;
  return input.hostJoinable;
}
